import React, { useState, useEffect } from 'react';
import { View, Text, TextInput, Pressable, Modal, StyleSheet } from 'react-native';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';

interface GenericEditModalProps {
    visible: boolean;
    onClose: () => void;
    currentValue: string;
    onSave: (newValue: string) => void;
    title: string;
    label: string;
    placeholder: string;
    helperText: string;
    maxLength?: number;
    multiline?: boolean;
    keyboardType?: 'default' | 'numeric' | 'email-address';
    suffix?: string;
    validation?: (value: string) => string; // Returns error message or empty string
}

const GenericEditModal: React.FC<GenericEditModalProps> = ({
    visible,
    onClose,
    currentValue,
    onSave,
    title,
    label,
    placeholder,
    helperText,
    maxLength = 100,
    multiline = false,
    keyboardType = 'default',
    suffix,
    validation
}) => {
    const [value, setValue] = useState(String(currentValue || ''));
    const [error, setError] = useState('');

    // Update state when modal opens or currentValue changes
    useEffect(() => {
        if (visible) {
            setValue(String(currentValue || ''));
            setError('');
        }
    }, [visible, currentValue]);

    const validateValue = (inputValue: string) => {
        if (validation) {
            return validation(String(inputValue || ''));
        }
        
        const trimmedValue = String(inputValue || '').trim();
        if (!trimmedValue) {
            return `${label} is required`;
        }
        return '';
    };

    const handleValueChange = (text: string) => {
        setValue(text);
        setError(validateValue(text));
    };

    const isValid = !error && String(value || '').trim() !== '';

    const handleSave = () => {
        const validationError = validateValue(value);
        if (validationError) {
            setError(validationError);
            return;
        }
        onSave(String(value || '').trim());
        onClose();
    };

    const handleCancel = () => {
        setValue(String(currentValue || ''));
        onClose();
    };

    const renderInput = () => {
        if (suffix) {
            return (
                <View style={[styles.inputContainer, error ? styles.inputContainerError : null]}>
                    <TextInput
                        style={styles.input}
                        value={value}
                        onChangeText={handleValueChange}
                        placeholder={placeholder}
                        autoFocus={true}
                        selectTextOnFocus={true}
                        multiline={multiline}
                        numberOfLines={multiline ? 8 : 1}
                        maxLength={maxLength}
                        keyboardType={keyboardType}
                        autoCapitalize={keyboardType === 'email-address' ? 'none' : 'sentences'}
                        textAlignVertical={multiline ? 'top' : 'center'}
                    />
                    <Text style={styles.suffix}>{suffix}</Text>
                </View>
            );
        }

        return (
            <TextInput
                style={[styles.input, error ? styles.inputError : null]}
                value={value}
                onChangeText={handleValueChange}
                placeholder={placeholder}
                autoFocus={true}
                selectTextOnFocus={true}
                multiline={multiline}
                numberOfLines={multiline ? 8 : 1}
                maxLength={maxLength}
                keyboardType={keyboardType}
                autoCapitalize={keyboardType === 'email-address' ? 'none' : 'sentences'}
                textAlignVertical={multiline ? 'top' : 'center'}
            />
        );
    };

    return (
        <Modal
            visible={visible}
            transparent={true}
            animationType="fade"
            onRequestClose={onClose}
        >
            <View style={styles.overlay}>
                <View style={[styles.modalContainer, multiline && styles.modalContainerLarge]}>
                    {/* Header */}
                    <View style={styles.header}>
                        <Text style={styles.title}>{title}</Text>
                        <Pressable onPress={onClose} style={styles.closeButton}>
                            <MaterialCommunityIcons name="close" size={20} color="#666" />
                        </Pressable>
                    </View>

                    {/* Content */}
                    <View style={styles.content}>
                        <Text style={styles.label}>{label}</Text>
                        {renderInput()}
                        
                        <View style={styles.characterCount}>
                            <Text style={styles.characterCountText}>{value.length}/{maxLength}</Text>
                        </View>
                        
                        {error ? (
                            <Text style={styles.errorText}>{error}</Text>
                        ) : (
                            <Text style={styles.helperText}>{helperText}</Text>
                        )}
                    </View>

                    {/* Footer */}
                    <View style={styles.footer}>
                        <Pressable onPress={handleCancel} style={[styles.button, styles.cancelButton]}>
                            <Text style={styles.cancelButtonText}>Cancel</Text>
                        </Pressable>
                        <Pressable 
                            onPress={handleSave} 
                            style={[styles.button, isValid ? styles.saveButton : styles.saveButtonDisabled]}
                            disabled={!isValid}
                        >
                            <Text style={[styles.saveButtonText, !isValid && styles.saveButtonTextDisabled]}>Save</Text>
                        </Pressable>
                    </View>
                </View>
            </View>
        </Modal>
    );
};

const styles = StyleSheet.create({
    overlay: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 20,
    },
    modalContainer: {
        backgroundColor: 'white',
        borderRadius: 12,
        width: '100%',
        maxWidth: 400,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.25,
        shadowRadius: 12,
        elevation: 8,
    },
    modalContainerLarge: {
        maxWidth: 600,
        maxHeight: '80%',
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: 20,
        paddingVertical: 16,
        borderBottomWidth: 1,
        borderBottomColor: '#e9ecef',
    },
    title: {
        fontSize: 18,
        fontWeight: '600',
        color: '#333',
        flex: 1,
        marginRight: 12,
    },
    closeButton: {
        padding: 4,
    },
    content: {
        padding: 20,
        flex: 1,
    },
    label: {
        fontSize: 16,
        fontWeight: '500',
        color: '#333',
        marginBottom: 8,
    },
    input: {
        borderWidth: 2,
        borderColor: '#e9ecef',
        borderRadius: 8,
        padding: 12,
        fontSize: 16,
        backgroundColor: '#f8f9fa',
        minHeight: 48,
    },
    inputError: {
        borderColor: '#dc3545',
        backgroundColor: '#fff5f5',
    },
    inputContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        borderWidth: 2,
        borderColor: '#e9ecef',
        borderRadius: 8,
        backgroundColor: '#f8f9fa',
        paddingHorizontal: 12,
        minHeight: 48,
    },
    inputContainerError: {
        borderColor: '#dc3545',
        backgroundColor: '#fff5f5',
    },
    suffix: {
        fontSize: 16,
        color: '#666',
        marginLeft: 8,
        fontWeight: '500',
    },
    characterCount: {
        alignItems: 'flex-end',
        marginTop: 4,
    },
    characterCountText: {
        fontSize: 11,
        color: '#999',
    },
    helperText: {
        fontSize: 12,
        color: '#666',
        marginTop: 8,
        textAlign: 'center',
    },
    errorText: {
        fontSize: 12,
        color: '#dc3545',
        marginTop: 8,
        textAlign: 'center',
        fontWeight: '500',
    },
    footer: {
        flexDirection: 'row',
        padding: 20,
        paddingTop: 0,
        gap: 12,
    },
    button: {
        flex: 1,
        paddingVertical: 12,
        borderRadius: 8,
        alignItems: 'center',
    },
    cancelButton: {
        backgroundColor: '#f8f9fa',
        borderWidth: 1,
        borderColor: '#e9ecef',
    },
    cancelButtonText: {
        fontSize: 16,
        fontWeight: '500',
        color: '#666',
    },
    saveButton: {
        backgroundColor: '#007bff',
    },
    saveButtonDisabled: {
        backgroundColor: '#cccccc',
    },
    saveButtonText: {
        fontSize: 16,
        fontWeight: '500',
        color: 'white',
    },
    saveButtonTextDisabled: {
        color: '#666666',
    },
});

export default GenericEditModal; 